import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Typography,
  Stack,
  Card,
  CardContent,
  Chip,
  Skeleton,
  Alert,
  Button,
  Badge,
  ButtonBase,
} from '@mui/material';
import MedicalInformationOutlinedIcon from '@mui/icons-material/MedicalInformationOutlined';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined';
import VerifiedIcon from '@mui/icons-material/Verified';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import Container from '@/components/PageSheet';
import { consultationsApi } from '@/services/api';
import { localized } from '@/utils/localizedContent';

// status -> chip colour + icon. 'ai_preliminary' is deliberately a warning
// tone: the report exists but is NOT yet confirmed by the physician.
const STATUS_META = {
  awaiting_capture: { color: 'info', Icon: PhotoCameraOutlinedIcon },
  awaiting_ai: { color: 'default', Icon: HourglassEmptyIcon },
  ai_preliminary: { color: 'warning', Icon: PendingActionsOutlinedIcon },
  confirmed: { color: 'success', Icon: VerifiedIcon },
  needs_in_person: { color: 'secondary', Icon: VerifiedIcon },
};

export default function ConsultationsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language;

  const {
    data: consultations,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['my-consultations'],
    queryFn: () => consultationsApi.listConsultations(),
  });

  const fmt = (iso) =>
    iso
      ? new Date(iso).toLocaleDateString(lang === 'fa' ? 'fa-IR' : 'en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })
      : '';

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 } }}>
      <Typography variant="h6" fontWeight={800}>
        {t('consultations.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('consultations.subtitle')}
      </Typography>

      {isError && (
        <Alert
          severity="error"
          action={<Button onClick={() => refetch()}>{t('common.retry')}</Button>}
        >
          {t('consultations.error')}
        </Alert>
      )}

      {isLoading ? (
        <Stack spacing={1.5}>
          {[0, 1].map((i) => (
            <Skeleton key={i} variant="rounded" height={110} />
          ))}
        </Stack>
      ) : (consultations || []).length === 0 ? (
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: '1px dashed',
            borderColor: 'divider',
            textAlign: 'center',
            py: 5,
          }}
        >
          <MedicalInformationOutlinedIcon sx={{ fontSize: 42, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">{t('consultations.empty')}</Typography>
          <Button sx={{ mt: 1 }} onClick={() => navigate('/services')}>
            {t('consultations.browseServices')}
          </Button>
        </Card>
      ) : (
        <Stack spacing={1.5}>
          {consultations.map((c) => {
            const meta = STATUS_META[c.status] || STATUS_META.awaiting_ai;
            const { Icon } = meta;
            return (
              <ButtonBase
                key={c.id}
                onClick={() => navigate(`/consultations/${encodeURIComponent(c.id)}`)}
                sx={{ display: 'block', width: '100%', textAlign: 'start', borderRadius: 3 }}
              >
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    width: '100%',
                  }}
                >
                  <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                      <Icon
                        fontSize="small"
                        color={meta.color === 'default' ? 'action' : meta.color}
                      />
                      <Typography fontWeight={800} sx={{ flex: 1 }} noWrap>
                        {localized(c.serviceName, lang) || t('consultations.title')}
                      </Typography>
                      {c.unreadCount > 0 && (
                        <Badge color="error" badgeContent={c.unreadCount}>
                          <ChatBubbleOutlineIcon fontSize="small" color="action" />
                        </Badge>
                      )}
                    </Stack>
                    <Chip
                      size="small"
                      color={meta.color}
                      variant={c.status === 'confirmed' ? 'filled' : 'outlined'}
                      label={t(`consultations.status.${c.status}`)}
                      sx={{ mb: 1 }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {localized(c.clinicName, lang)}
                      {c.physicianName ? ` · ${c.physicianName}` : ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {fmt(c.createdAt)}
                    </Typography>
                  </CardContent>
                </Card>
              </ButtonBase>
            );
          })}
        </Stack>
      )}
    </Container>
  );
}
